import Button from '@liquidweb-dev/undercurrent/Button';
import Dialog, { type DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import DialogActions from '@liquidweb-dev/undercurrent/DialogActions';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import ArrowUpRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpRight';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import getAppConfigs from 'modules/appConfig/reducer';

const PRISM_SSO_HOST = 'https://nutanix-login.nexcess.com';

/**
 * Entry point that carries the customer through SSO and into Prism Central.
 * Owned by the Silver Crystal bridge service — see RDX-1160. The bridge signs
 * the customer in against the same OIDC provider the portal uses, so the
 * authority comes from the app config rather than being fixed here.
 */
export const getPrismUrl = (uniqId: string) => {
	const authority = (getAppConfigs()?.openId?.authority || '').replace(
		/^https?:\/\//,
		'',
	);
	const url = new URL(`${PRISM_SSO_HOST}/go/${uniqId}`);

	url.searchParams.set('authority', authority);

	return url.toString();
};

export interface DialogNutanixPrismProps extends Omit<DialogProps, 'onClose'> {
	onClose: () => void;
	uniqId: string;
}

/**
 * Confirms the handoff from the portal to Nutanix Prism before opening Prism
 * in a new tab.
 */
const DialogNutanixPrism = ({
	onClose,
	uniqId,
	...rest
}: DialogNutanixPrismProps) => {
	const handleOpenPrism = () => {
		window.open(getPrismUrl(uniqId), '_blank', 'noopener');
		onClose();
	};

	return (
		<Dialog onClose={onClose} fullWidth maxWidth="sm" {...rest}>
			<DialogTitle onClose={onClose}>
				Nutanix Private Cloud — Prism Central
			</DialogTitle>
			<DialogContent>
				<Typography variant="body-md">
					You&apos;re being redirected to Nutanix Prism Central to manage your
					private cloud. Prism provides full infrastructure management,
					including VM provisioning, resource allocation, and cluster health for
					your environment.
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button variant="secondary" size="large" onClick={onClose}>
					Cancel
				</Button>
				<Button
					variant="primary"
					size="large"
					endIcon={<ArrowUpRightIcon />}
					onClick={handleOpenPrism}
				>
					Open Prism Central
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DialogNutanixPrism;
